import { useRef } from 'react'
import * as PropTypes from 'prop-types'
import moment from 'moment-timezone'
import * as Yup from 'yup'
import { Formik } from 'formik'
import AsyncSelect from 'react-select/async'
import { useTranslation } from 'react-i18next'

import { getApiErrorMessage } from '@/utils'

import { formValidation } from '../../FormLegacy/validation'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import { APP_COLORS } from '../../../helpers/constants'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import { customStyles, DropdownIndicator } from './helpers'

const ShiftNoteForm = ({
  handleCancel,
  residents,
  staffPersons,
  currentUser,
  building,
  editedLog,
  refreshDataActionLogs,
  loadResidentOptions,
}) => {
  const { t } = useTranslation(['action-log', 'common'])
  const { displaySnackbar } = useSnackbar()
  const ref = useRef(null)

  const validationSchema = Yup.object().shape({
    title: formValidation.required,
    description: formValidation.required,
    date: formValidation.required,
  })

  const getResidentObject = ({ clientId }) => {
    if (!clientId) return null
    return { label: `${clientId.user.firstName} ${clientId.user.lastName}`, value: clientId.id }
  }

  const handleFormSubmit = async (
    { title, description, date, shiftManagerId, residentData },
    { setSubmitting },
  ) => {
    const dataToSend = {
      name: title,
      description,
      category: 'shiftNote',
      date: building
        ? moment(new Date(date)).tz(building.timezone, true).valueOf()
        : moment(new Date(date)).valueOf(),
      shiftManagerId,
      clientId: residentData ? residentData.value : null,
      buildingId: building ? building.id : null,
    }

    try {
      editedLog
        ? await API.updateShiftNote(editedLog.id, dataToSend)
        : await API.createNewShiftNote(dataToSend)
      setSubmitting(false)
      handleCancel()
      refreshDataActionLogs()
    } catch (err) {
      setSubmitting(false)
      displaySnackbar(getApiErrorMessage(err), true)
    }
  }

  const handleDelete = async () => {
    try {
      await API.deleteShiftNote(editedLog.id)
      handleCancel()
      refreshDataActionLogs()
    } catch (err) {
      displaySnackbar(getApiErrorMessage(err), true)
    }
  }

  return (
    <div className="shift-note">
      <Formik
        enableReinitialize={true}
        initialValues={{
          title: editedLog ? editedLog.name : '',
          description: editedLog ? editedLog.description : '',
          residentData: editedLog ? getResidentObject(editedLog) : null,
          shiftManagerId: editedLog
            ? editedLog.shiftManagerId
            : currentUser.user.role === 'ShiftManager'
              ? currentUser.id
              : null,
          date: editedLog
            ? new Date(moment(editedLog.date).tz(building.timezone).format('MM/DD/yyyy h:mm a'))
            : new Date(moment(new Date()).tz(building.timezone, true).format('MM/DD/yyyy h:mm a')),
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
          isSubmitting,
          isValid,
          dirty,
        }) => {
          return (
            <div className="shift-note__container" ref={ref}>
              <div className="title">
                {editedLog
                  ? t('action-log:shiftNoteForm.editTitle')
                  : t('action-log:shiftNoteForm.createTitle')}
              </div>
              <div className="columns">
                <div className="column is-12">
                  <TextField
                    maxLength="100"
                    name={'title'}
                    label={t('action-log:shiftNoteForm.titleLabel')}
                    placeholder={t('action-log:shiftNoteForm.titlePlaceholder')}
                    onChange={handleChange}
                    value={values.title}
                    isError={touched.title && !!errors.title}
                    onBlur={() => setFieldTouched('title')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  <TextAreaField
                    name={'description'}
                    label={t('action-log:shiftNoteForm.descriptionLabel')}
                    placeholder={t('action-log:shiftNoteForm.descriptionPlaceholder')}
                    onChange={handleChange}
                    value={values.description}
                    isError={touched.description && !!errors.description}
                    onBlur={() => setFieldTouched('description')}
                    maxLength="1000"
                    style={{ height: '100px' }}
                  />
                </div>
              </div>
              <div className="header">{t('action-log:shiftNoteForm.additionalDetailsHeader')}</div>
              <div className="columns">
                <div className="column is-6">
                  <div className="resident-picker-container">
                    <label>{t('action-log:shared.residentLabel')}</label>
                    <AsyncSelect
                      loadOptions={loadResidentOptions}
                      defaultOptions={residents}
                      options={residents}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      value={values.residentData}
                      defaultInputValue=""
                      placeholder={t('action-log:shared.searchResidentPlaceholder')}
                      isClearable
                      onChange={(residentData) => setFieldValue('residentData', residentData)}
                    />
                  </div>
                </div>
                <div className="column is-6">
                  <SelectField
                    name="shiftManagerId"
                    options={staffPersons}
                    label={t('action-log:shared.staffLabel')}
                    placeholderStyle={{ color: APP_COLORS.black300 }}
                    placeholder={t('action-log:shiftNoteForm.staffPlaceholder')}
                    onChange={(e) => handleChange(e)}
                    value={values.shiftManagerId}
                    isError={touched.shiftManagerId && !!errors.shiftManagerId}
                    onBlur={() => setFieldTouched('shiftManagerId')}
                    disabled={currentUser.user.role === 'ShiftManager'}
                  />
                </div>
              </div>
              <div className="columns">
                <div className="column is-12">
                  <DateField
                    name="date"
                    label={t('action-log:shiftNoteForm.dateLabel')}
                    placeholderText={t('action-log:shiftNoteForm.datePlaceholder')}
                    autoComplete="off"
                    showTimeSelect
                    dateFormat="MM/dd/yyyy h:mm a"
                    minDate={new Date()}
                    value={values.date}
                    onChange={(e) => setFieldValue('date', e.target.value)}
                    onChangeRaw={(e) => e.preventDefault()}
                    onBlur={() => setFieldTouched('date')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className={editedLog ? 'column is-4' : 'column is-6'}>
                  <button
                    type="submit"
                    className={`button button-gray full-width ${isSubmitting ? 'is-loading' : ''}`}
                    onClick={handleCancel}
                  >
                    {t('common:actions.cancel')}
                  </button>
                </div>
                <div className={editedLog ? 'column is-4' : 'column is-6'}>
                  <button
                    type="submit"
                    className={`button button-primary full-width ${
                      isSubmitting ? 'is-loading' : ''
                    }`}
                    onClick={handleSubmit}
                    disabled={!isValid || !dirty || isSubmitting}
                  >
                    {editedLog
                      ? t('action-log:shiftNoteForm.updateButton')
                      : t('action-log:shiftNoteForm.createButton')}
                  </button>
                </div>
                {editedLog && (
                  <div className={editedLog ? 'column is-4' : 'column is-6'}>
                    <button
                      type="submit"
                      className={`button button-primary full-width ${
                        isSubmitting ? 'is-loading' : ''
                      }`}
                      onClick={() => handleDelete()}
                    >
                      {t('action-log:shiftNoteForm.deleteButton')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

ShiftNoteForm.propTypes = {
  handleCancel: PropTypes.func,
  loadResidentOptions: PropTypes.func,
  residents: PropTypes.array,
  staffPersons: PropTypes.array,
  currentUser: PropTypes.object,
  building: PropTypes.object,
  editedLog: PropTypes.object,
  refreshDataActionLogs: PropTypes.func,
}

export default ShiftNoteForm
